import { useEffect, useState } from 'react';
import { useRxCollection } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { InspectionTaskListType } from '../../models/models';
import { sentryCaptureException } from '../../utilities';
import { InspectionStatus, WorkPackageStatus } from '../../utilities/enums';
import { getValueFromLocalStorage, setValueinLocalStorage } from '../../utilities/utilityFunctions';
import { RxdbCollectionName } from '../rxdbCollectionName';
import { TaskList, TaskListDocument } from '../taskList/queryBuilder';
import { useTaskList } from '../taskList/useTaskList';
import { useWorkPackages } from '../workPackages/useWorkPackages';
import { InspectionListCollection, InspectionListDocument } from './queryBuilder';

export const useInspectionList = () => {
  const inspectionListCollection: InspectionListCollection = useRxCollection(
    RxdbCollectionName.INSPECTION_LIST,
  );
  const { taskListCollection } = useTaskList();
  const [inspectionTaskListInitialized, setInspectionTaskListInitialized] =
    useState<boolean>(false);
  const { setWorkPackages, setInspectionCountInWorkPackage, workPackagesCollection } =
    useWorkPackages();
  const { SiteDiscipline, CurrentUserSelected } = useMst();

  const workPackageId = CurrentUserSelected.workPackageId;
  const selectedDisciplineJsonString: string =
    getValueFromLocalStorage('selectedDiscipline') || '{}';
  const selectedDiscipline: { selectedDiscipline: string; selectedDisciplineId: number } =
    JSON.parse(selectedDisciplineJsonString);
  const [inspectionListInitialized, setinspectionListInitialized] = useState<boolean>(false);
  const site = SiteDiscipline.siteId;
  const discipline = SiteDiscipline.disciplineId;

  useEffect(() => {
    if (inspectionListCollection) setinspectionListInitialized(true);
  }, [inspectionListCollection]);

  useEffect(() => {
    if (taskListCollection && inspectionListCollection) setInspectionTaskListInitialized(true);
  }, [taskListCollection, inspectionListCollection]);
  const disciplineMapping = (disciplineId: number) => {
    switch (disciplineId) {
      case 1:
        return 'Electrical';
      case 2:
        return 'Mechanical';
      case 3:
        return 'Structural';
      case 4:
        return 'Shaft';
      default:
        return '';
    }
  };

  const setInspectionList = async (inspectionId: string, status: number) => {
    try {
      if (inspectionListCollection) {
        const updated = await inspectionListCollection
          .findOne({
            selector: { inspectionId },
          })
          .update({
            $set: {
              assetInspectionStatusId: status,
            },
          });
        const result = await inspectionListCollection
          .find({
            selector: {
              workPackageId,
            },
          })
          .exec();
        if (status === 4) {
          await setInspectionCountInWorkPackage(workPackageId.toString());
        }
        if (result.filter(item => item.assetInspectionStatusId !== 4).length === 0) {
          await setWorkPackages(workPackageId.toString(), 4);
          let count = selectedDiscipline?.selectedDiscipline?.substring(
            selectedDiscipline?.selectedDiscipline?.indexOf('(') + 1,
          );
          count = count.substring(0, count.length - 1);
          const newValue = {
            selectedDiscipline: `${disciplineMapping(selectedDiscipline?.selectedDisciplineId)} (${
              Number(count) - 1
            })`,
            selectedDisciplineId: selectedDiscipline.selectedDisciplineId,
          };
          setValueinLocalStorage('selectedDiscipline', JSON.stringify(newValue));
          setValueinLocalStorage('wpCount', JSON.stringify(Number(count) - 1));
          SiteDiscipline.setWpCount(Number(count) - 1);
          SiteDiscipline.setSiteDisciplineData();
        } else {
          if (workPackagesCollection) {
            await workPackagesCollection
              .findOne({
                selector: {
                  workPackageId: { $eq: workPackageId.toString() },
                },
              })
              .update({
                $set: {
                  workPackageStatusId: WorkPackageStatus.IN_PROGRESS,
                },
              });
          }
        }
        return updated;
      }
    } catch (error) {
      console.log('🚀 ~ file: useInspectionList.ts ~ line 103 ~ setInspectionList ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionList.tsx ~ line 220 ~ setInspectionList ~',
      );
      throw error;
    }
  };

  const getInspectionTaskList = async (wpId: number): Promise<InspectionTaskListType[]> => {
    if (!inspectionListCollection || !taskListCollection) return [];
    let inspectionList: InspectionListDocument[];
    let taskList: TaskListDocument[];
    try {
      inspectionList = await inspectionListCollection
        ?.find({
          selector: {
            $and: CurrentUserSelected.isAdhoc
              ? [
                  { isAdhoc: CurrentUserSelected.isAdhoc },
                  { assetInspectionStatusId: { $ne: InspectionStatus.COMPLETED } },
                  { site },
                  { discipline },
                ]
              : [
                  { workPackageId: wpId },
                  { assetInspectionStatusId: { $ne: InspectionStatus.COMPLETED } },
                ],
          },
        })
        .exec();

      taskList = await taskListCollection
        ?.find({
          selector: {
            $and: CurrentUserSelected.isAdhoc
              ? [{ isActive: true }, { site }, { discipline }]
              : [{ fkSchedule: wpId }, { isActive: true }],
          },
        })
        .exec();

      const inspectionTask = JSON.parse(JSON.stringify(inspectionList)).map(
        (inspection: InspectionListDocument) => {
          const tempTaskData = taskList
            .filter((task: TaskList) => task.fkAssetInspection === Number(inspection.inspectionId))
            .map((task: TaskList) => {
              return {
                pkAssetInspectionTask: task.pkAssetInspectionTask,
                fkSchedule: task.fkSchedule,
                fkAssetInspection: task.fkAssetInspection,
                fkInspectorLead: task.fkInspectorLead,
                fkInspectorField: task.fkInspectorField,
                name: task.name,
                taskDescription: task.taskDescription,
                commentInspectorField: task.commentInspectorField,
                pkUuId: task.pkUuId,
                fkInspectionTaskStatusCategory: task.fkInspectionTaskStatusCategory,
                attentionFlag: task.attentionFlag,
                isAssignedBack: task.isAssignedBack,
                isActive: task.isActive,
                recommendations: task.recommendations,
                siteCoordinatorUserId: task.siteCoordinatorUserId,
                fieldInspectorUserId: task.fieldInspectorUserId,
                leadInspectorName: task?.leadInspectorName || '',
                clerkId: task.clerkId,
                clerkName: task.clerkName,
              };
            });
          return {
            ...inspection,
            dateCreated: undefined,
            dateModified: undefined,
            discipline: undefined,
            site: undefined,
            assetInspectionStatus: undefined,
            _attachments: undefined,
            _rev: undefined,
            taskList: tempTaskData,
            inspectorNameList: [
              ...new Map(
                tempTaskData.map(
                  (task: {
                    fkInspectorField?: number;
                    name?: string;
                    clerkId?: number;
                    clerkName?: string;
                  }) => [
                    task.fkInspectorField,
                    {
                      inspectorId: task.fkInspectorField,
                      name: task.name,
                      clerkId: task.clerkId,
                      clerkName: task.clerkName,
                    },
                  ],
                ),
              ).values(),
            ],
            isAssignedBackTask:
              tempTaskData.find((task: { isAssignedBack: boolean }) => task.isAssignedBack) ||
              false,
          };
        },
      );

      return JSON.parse(JSON.stringify(inspectionTask));
    } catch (error) {
      console.log(
        '🚀 ~ file: useInspectionList.ts ~ line 216 ~ getInspectionTaskList ~ error',
        error,
      ); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionList.tsx ~ line 222 ~ getInspectionTaskList ~',
      );
      throw error;
    }
  };

  const getAllInspectionList = async (): Promise<InspectionListDocument[]> => {
    if (!inspectionListCollection) return [];
    let inspectionList: InspectionListDocument[];
    try {
      const queryObj = {
        selector: {
          $and: [{ site }, { discipline }],
        },
      };

      inspectionList = await inspectionListCollection?.find(queryObj).exec();
      return inspectionList;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useInspectionList.ts ~ line 151 ~ getAllInspectionList ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionList.tsx ~ line 210 ~ getAllInspectionList ~',
      );
      throw error;
    }
  };

  const getInspectionById = async (
    inspectionId: string,
  ): Promise<InspectionListDocument | null> => {
    if (!inspectionListCollection) return null;
    try {
      const inspection = await inspectionListCollection
        ?.findOne({ selector: { inspectionId } })
        .exec();
      return inspection;
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useInspectionList.tsx ~ line 278 ~ getInspectionById ~',
      );
      throw error;
    }
  };

  return {
    inspectionListInitialized,
    getAllInspectionList,
    inspectionListCollection,
    setInspectionList,
    inspectionTaskListInitialized,
    getInspectionTaskList,
    getInspectionById,
  };
};

export default {};
